/**
 * Dashboard de Detalle de Empleado
 * Fecha: 16 Oct 2025
 *
 * Vista completa con información personal, laboral, documentos y contratos
 */
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { formatDate } from '@/shared/lib/dateUtils'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { employeesService } from '@/features/rh/api/employees.service';
import type {
  Employee,
  EstadoCivil,
  UpdateEmployeeDto,
} from '@/types/employee.types';
import type { Area } from '@/features/configuracion/catalogos/types/catalogs.types';
import { LoadingSpinner, ConfirmModal } from '@/shared/components';
import { Notification, useNotification } from '@/shared/components/Notification';
import { Phone, ArrowLeft, Upload, PencilLine } from "lucide-react";
import {
  usePermissions,
  EMPLOYEE_PERMISSIONS,
} from '@/shared/hooks/usePermissions';
import { EmployeeDigitalFile } from '@/features/rh/components/employees/EmployeeDigitalFile';
import { EmployeeContractsDetail } from '@/features/rh/components/employees/EmployeeContractsDetail';
import { useForm, Controller } from "react-hook-form";
import SelectBuscable from '@/shared/components/SelectBuscable';
import { Badge } from '@/shared/components/Badge';
import { filterOutMasterUsers } from '@/shared/lib/userFilters';
import { config } from '@/config/env'

type ButtonsTypeMenu =
  | "Emergencia"
  | "InfoLaboral"
  | "Expediente"
  | "DocOpcionales"
  | "AccionesAdmin"
  | "Contratos";

const ESTADOS_CIVILES: { value: EstadoCivil; label: string }[] = [
  { value: "soltero", label: "Soltero/a" },
  { value: "casado", label: "Casado/a" },
  { value: "union_libre", label: "Unión Libre" },
  { value: "divorciado", label: "Divorciado/a" },
  { value: "viudo", label: "Viudo/a" },
];

const isNullishText = (value?: string | null): boolean => {
  if (value === null || value === undefined) return true;
  const normalized = String(value).trim().toLowerCase();
  return (
    normalized === "" || normalized === "null" || normalized === "undefined"
  );
};

const formatDisplayText = (
  value?: string | null,
  fallback = "Sin asignar",
): string => {
  if (isNullishText(value)) return fallback;
  return String(value)
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^\w/, (char) => char.toUpperCase());
};

export const EmployeeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { notification, showSuccess, showError, hideNotification } =
    useNotification();

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEnablingImss, setIsEnablingImss] = useState(false);
  const [isEnablingErp, setIsEnablingErp] = useState(false);
  const [showImssModal, setShowImssModal] = useState(false);
  const [showErpModal, setShowErpModal] = useState(false);
  const [imssCanEnable, setImssCanEnable] = useState<{
    canEnable: boolean;
    reasons: string[];
  }>({
    canEnable: false,
    reasons: [],
  });
  const [erpCanEnable, setErpCanEnable] = useState<{
    canEnable: boolean;
    reasons: string[];
  }>({
    canEnable: false,
    reasons: [],
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedButtonMenu = (searchParams.get("tab") as ButtonsTypeMenu | null) ?? "Emergencia";
  const setSelectedButtonMenu = (tab: ButtonsTypeMenu) =>
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set("tab", tab); return next; }, { replace: true });

  // Estados para catálogos
  const [areas, setAreas] = useState<Area[]>([]);
  const [puestos, setPuestos] = useState<any[]>([]);
  const [puestosFiltrados, setPuestosFiltrados] = useState<any[]>([]);
  const [usuarios, setUsuarios] = useState<any[]>([]);

  /**
   * Un solo modo de edición para toda la ficha.
   *
   * Antes cada tarjeta se editaba por su cuenta —`editableSection` guardaba
   * cuál— y eso obligaba a guardar sección por sección: cambiar el área y el
   * teléfono eran dos lápices, dos guardados y dos recargas.
   */
  const [editando, setEditando] = useState(false);
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    resetField,
  } = useForm();

  const canUpdate = hasPermission(EMPLOYEE_PERMISSIONS.EMPLOYEES_UPDATE);
  // Permisos deshabilitados temporalmente a petición
  const canEnableImss = true;
  const canEnableErp = true;

  useEffect(() => {
    if (id) {
      loadEmployee();
    }
  }, [id]);

  // Handle legacy location.state approach + new URL param approach for autoEdit
  useEffect(() => {
    const state = location.state as any;
    if (state?.activeTab && !searchParams.get("tab")) {
      setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set("tab", state.activeTab); return next; }, { replace: true });
    }
    if (state?.autoEdit || searchParams.get("autoEdit") === "true") {
      setEditando(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEmployee = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await employeesService.getById(Number(id));

      const [areasRes, puestosRes, usuariosRes] = await Promise.all([
        fetch(
          `${
            config.apiBaseUrl
          }/catalogos/areas`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
              "Content-Type": "application/json",
            },
          },
        ),
        fetch(
          `${
            config.apiBaseUrl
          }/catalogos/puestos`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
              "Content-Type": "application/json",
            },
          },
        ),
        fetch(
          `${
            config.apiBaseUrl
          }/users`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
              "Content-Type": "application/json",
            },
          },
        ),
      ]);

      const areasData = await areasRes.json();
      const puestosData = await puestosRes.json();
      const usuariosData = await usuariosRes.json();

      setAreas(
        Array.isArray(areasData) ? areasData.filter((a: Area) => a.activa) : [],
      );
      setPuestos(
        Array.isArray(puestosData)
          ? puestosData.filter((p: any) => p.activo)
          : [],
      );
      setUsuarios(
        filterOutMasterUsers(
          Array.isArray(usuariosData)
            ? usuariosData.filter((u: any) => u.isActive)
            : [],
        ),
      );

      setEmployee(data);

      reset({
        curp: data?.curp ?? "",
        rfc: data?.rfc ?? "",
        nss: data?.nss ?? "",
        fechaNacimiento: data?.fechaNacimiento ?? "",
        telefonoPersonal: data?.telefonoPersonal ?? "",
        estadoCivil: data?.estadoCivil ?? "",
        direccion: data?.direccion ?? "",
        areaId: data?.areaId ?? "",
        puestoId: data?.puestoId ?? "",
        fechaIngreso: data?.fechaIngreso ?? "",
        tipoContrato: data?.tipoContrato ?? "",
        jornada: data?.jornada ?? "",
        sueldoBrutoMensual: data?.sueldoBrutoMensual ?? "",
        jefeInmediatoId: data?.jefeInmediatoId ?? "",
        prestaciones: data?.prestaciones ?? "",
        emergenciaContactoNombre: data?.emergenciaContactoNombre ?? "",
        emergenciaContactoRelacion: data?.emergenciaContactoRelacion ?? "",
        emergenciaContactoTelefono: data?.emergenciaContactoTelefono ?? "",
      });

      // Cargar permisos IMSS/ERP
      if (canEnableImss) {
        const imssCheck = await employeesService.canEnableImss(Number(id));
        setImssCanEnable(imssCheck);
      }
      if (canEnableErp) {
        const erpCheck = await employeesService.canEnableErp(Number(id));
        setErpCanEnable(erpCheck);
      }
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al cargar empleado:", err);
      setError(err.message || "Error al cargar empleado");
    } finally {
      setIsLoading(false);
    }
  };

  const filterPuestosByArea = (areaId: number) => {
    if (areaId) {
      const filtered = puestos.filter((p) => p.areaId === areaId && p.activo);
      setPuestosFiltrados(filtered);
    } else {
      setPuestosFiltrados([]);
    }
  };

  const handleEnableImss = async () => {
    if (!employee) return;
    try {
      setIsEnablingImss(true);
      await employeesService.enableImss(employee.id);
      await loadEmployee();
      setShowImssModal(false);
      showSuccess("IMSS Habilitado", "El IMSS ha sido habilitado exitosamente");
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al habilitar IMSS:", err);
      showError(
        "Error al Habilitar IMSS",
        err.message || "Error al habilitar IMSS",
      );
    } finally {
      setIsEnablingImss(false);
    }
  };

  const handleDisableImss = async () => {
    if (!employee) return;
    try {
      setIsEnablingImss(true);
      await employeesService.disableImss(employee.id);
      await loadEmployee();
      setShowImssModal(false);
      showSuccess(
        "IMSS Deshabilitado",
        "El IMSS ha sido deshabilitado exitosamente",
      );
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al deshabilitar IMSS:", err);
      showError(
        "Error al Deshabilitar IMSS",
        err.message || "Error al deshabilitar IMSS",
      );
    } finally {
      setIsEnablingImss(false);
    }
  };

  const handleEnableErp = async () => {
    if (!employee) return;
    try {
      setIsEnablingErp(true);
      await employeesService.enableErp(employee.id);
      await loadEmployee();
      setShowErpModal(false);
      showSuccess("ERP Habilitado", "El ERP ha sido habilitado exitosamente");
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al habilitar ERP:", err);
      showError(
        "Error al Habilitar ERP",
        err.message || "Error al habilitar ERP",
      );
    } finally {
      setIsEnablingErp(false);
    }
  };

  const handleDisableErp = async () => {
    if (!employee) return;
    try {
      setIsEnablingErp(true);
      await employeesService.disableErp(employee.id);
      await loadEmployee();
      setShowErpModal(false);
      showSuccess(
        "ERP Deshabilitado",
        "El ERP ha sido deshabilitado exitosamente",
      );
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al deshabilitar ERP:", err);
      showError(
        "Error al Deshabilitar ERP",
        err.message || "Error al deshabilitar ERP",
      );
    } finally {
      setIsEnablingErp(false);
    }
  };

  const handleRecalculateCompleteness = async () => {
    if (!employee) return;
    try {
      await employeesService.recalculateCompleteness(employee.id);
      await loadEmployee();
      showSuccess(
        "Completitud Recalculada",
        "La completitud del expediente ha sido recalculada",
      );
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al recalcular completitud:", err);
      showError(
        "Error al Recalcular",
        err.message || "Error al recalcular completitud",
      );
    }
  };

  /** Todo lo editable de la ficha en un solo cuerpo. */
  const buildPayload = (data: any): UpdateEmployeeDto => ({
    curp: data?.curp ?? undefined,
    rfc: data?.rfc ?? undefined,
    nss: data?.nss ?? undefined,
    fechaNacimiento: data?.fechaNacimiento ?? undefined,
    telefonoPersonal: data?.telefonoPersonal ?? undefined,
    estadoCivil: data?.estadoCivil ?? undefined,
    direccion: data?.direccion ?? undefined,
    emergenciaContactoNombre: data?.emergenciaContactoNombre || undefined,
    emergenciaContactoRelacion: data?.emergenciaContactoRelacion || undefined,
    emergenciaContactoTelefono: data?.emergenciaContactoTelefono || undefined,
    areaId: data?.areaId ? Number(data.areaId) : undefined,
    puestoId: data?.puestoId ? Number(data.puestoId) : undefined,
    fechaIngreso: data?.fechaIngreso ?? undefined,
    tipoContrato: data?.tipoContrato ?? "tiempo_completo",
    jornada: data?.jornada ?? undefined,
    sueldoBrutoMensual: data?.sueldoBrutoMensual
      ? Number(data.sueldoBrutoMensual)
      : undefined,
    jefeInmediatoId: data?.jefeInmediatoId ?? undefined,
    prestaciones: data?.prestaciones ?? undefined,
  });

  const onSubmit = async (data: any) => {
    try {
      if (id) {
        await employeesService.update(Number(id), buildPayload(data));
        await cancelEditReloadData();
      }
    } catch (errCrudo: unknown) {
      const err = errCrudo as { message?: string };
      console.error("Error al actualizar empleado:", err);
      setError(err.message || "Error al actualizar empleado");
    }
  };

  const cancelEditReloadData = async () => {
    setEditando(false);
    await loadEmployee();
  };

  const startEditing = (tab?: ButtonsTypeMenu) => {
    if (tab) setSelectedButtonMenu(tab);
    setEditando(true);
  };

  // Todas las secciones siguen al mismo interruptor.
  const isPersonalEditing = editando;
  const isEmergencyEditing = editando;
  const isLaboralEditing = editando;
  const isExpedienteEditing = editando;
  const isOptionalDocsEditing = editando;
  const isAdminActionsEditing = editando;
  const isContractsEditing = editando;

  useEffect(() => {
    if (watch("areaId")) {
      const areaId = Number(watch("areaId"));
      filterPuestosByArea(areaId);

      resetField("puestoId");
    }
  }, [watch("areaId")]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Cargando información del empleado..." />
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-6 shadow-sm">
            <h3 className="text-lg font-bold text-red-700 mb-2">
              Error al cargar empleado
            </h3>
            <p className="text-red-600 mb-4 leading-relaxed">
              {error || "No se encontró el empleado"}
            </p>
            <button
              onClick={() => navigate("/employees")}
              className={estiloDeBoton('primario')}
            >
              Volver a la lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 my-6">
      <form className="w-full">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <button
              type="button"
              onClick={() => navigate("/employees")}
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
            <div>
              <TituloDePantalla nivel="interna" className="mb-2">
                {employee.user?.firstName} {employee.user?.lastName}
              </TituloDePantalla>
              <p className="text-gray-600 dark:text-gray-400">
                {employee.user?.email}
              </p>
            </div>
          </div>
        </div>

        {/* Información Personal */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-1 border-gray-200 mb-8">
            <div className="flex items-center justify-between mb-4 border-b border-gray-200 p-6">
              <h2 className="text-base font-semibold text-[#1E1E1F] flex items-center">
                Información Personal
              </h2>
              <div className="flex items-center gap-3">
                <Badge color="gris">Preregistro</Badge>
              {canUpdate && !editando && (
                <button
                  type="button"
                  onClick={() => startEditing()}
                  className="inline-flex items-center text-[#315FB9]"
                  title="Editar ficha del empleado"
                >
                  <PencilLine className="w-5 h-5" />
                </button>
              )}
              </div>
            </div>

            <div className="flex justify-between items-start flex-wrap p-6 pt-0">
              <div className="w-1/4 px-1 mt-4">
                <p className="text-sm text-[#777777]">Nombre Completo:</p>
                <p className="text-gray-900 dark:text-white">
                  {employee.user?.firstName} {employee.user?.lastName}
                </p>
              </div>

              <div className="w-1/4 px-1 mt-4">
                <p className="text-sm text-[#777777]">Email:</p>
                <p className="text-gray-900 dark:text-white">
                  {employee.user?.email}
                </p>
              </div>

              <div className="w-1/4 px-1 mt-4">
                <p className="text-sm text-[#777777]">CURP:</p>
                {isPersonalEditing ? (
                  <div>
                    <input
                      {...register("curp", { required: true })}
                      id="curp"
                      name="curp"
                      className="w-full h-10 px-3 border-0 rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-1 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                      placeholder="-----"
                    />
                    {errors.curp && (
                      <p className="text-red-500 text-xs mt-1">
                        Este campo es requerido
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-900 dark:text-white">
                    {employee.curp}
                  </p>
                )}
              </div>

              <div className="w-1/4 px-1 mt-4">
                <p className="text-sm text-[#777777]">RFC:</p>
                {isPersonalEditing ? (
                  <div>
                    <input
                      {...register("rfc", { required: true })}
                      id="rfc"
                      name="rfc"
                      className="w-full h-10 px-3 border-0 rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-1 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                      placeholder="-----"
                    />
                    {errors.rfc && (
                      <p className="text-red-500 text-xs mt-1">
                        Este campo es requerido
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-900 dark:text-white">
                    {employee.rfc}
                  </p>
                )}
              </div>

              {(employee.nss || isPersonalEditing) && (
                <div className="w-1/4 px-1 mt-4">
                  <p className="text-sm text-[#777777]">NSS:</p>
                  {isPersonalEditing ? (
                    <div>
                      <input
                        {...register("nss", { required: true })}
                        id="nss"
                        name="nss"
                        className="w-full h-10 px-3 border-0 rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-1 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                        placeholder="-----"
                      />
                      {errors.nss && (
                        <p className="text-red-500 text-xs mt-1">
                          Este campo es requerido
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-900 dark:text-white">
                      {employee.nss}
                    </p>
                  )}
                </div>
              )}

              <div className="w-1/4 px-1 mt-4">
                <p className="text-sm text-[#777777]">Fecha de Nacimiento:</p>
                {isPersonalEditing ? (
                  <div>
                    <input
                      {...register("fechaNacimiento", { required: true })}
                      id="fechaNacimiento"
                      name="fechaNacimiento"
                      type="date"
                      className="w-full h-10 px-3 border-0 rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-1 transition-all duration-200 text-sm text-gray-900"
                    />
                    {errors.fechaNacimiento && (
                      <p className="text-red-500 text-xs mt-1">
                        Este campo es requerido
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-900 dark:text-white">
                    {formatDate(employee.fechaNacimiento)}
                  </p>
                )}
              </div>

              <div className="w-1/4 px-1 mt-4">
                <p className="text-sm text-[#777777]">Estado Civil:</p>
                {isPersonalEditing ? (
                  <div>
                    <Controller
                      name="estadoCivil"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <SelectBuscable
                          variante="formulario"
                          id="estadoCivil"
                          name="estadoCivil"
                          opciones={ESTADOS_CIVILES}
                          value={String(field.value ?? "")}
                          onChange={field.onChange}
                          canDelete={false}
                          placeholderDeBusqueda="Buscar estado civil..."
                        />
                      )}
                    />
                    {errors.estadoCivil && (
                      <p className="text-red-500 text-xs mt-1">
                        Este campo es requerido
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-900 dark:text-white">
                    {employee.estadoCivil}
                  </p>
                )}
              </div>

              {(employee.telefonoPersonal || isPersonalEditing) && (
                <div className="w-1/4 px-1 mt-4">
                  <p className="text-sm text-[#777777]">Teléfono:</p>
                  {isPersonalEditing ? (
                    <div>
                      <input
                        {...register("telefonoPersonal", { required: true })}
                        id="telefonoPersonal"
                        name="telefonoPersonal"
                        className="w-full h-10 px-3 border-0 rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-1 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                      />
                      {errors.telefonoPersonal && (
                        <p className="text-red-500 text-xs mt-1">
                          Este campo es requerido
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-900 dark:text-white">
                      {employee.telefonoPersonal}
                    </p>
                  )}
                </div>
              )}

              {(employee.direccion || isPersonalEditing) && (
                <div className="w-full mt-4">
                  <p className="text-sm text-[#777777] mb-1">Dirección</p>
                  {isPersonalEditing ? (
                    <div className="w-full">
                      <input
                        {...register("direccion", { required: true })}
                        id="direccion"
                        name="direccion"
                        className="w-full h-10 px-3 border-0 rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-1 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                      />
                      {errors.direccion && (
                        <p className="text-red-500 text-xs mt-1">
                          Este campo es requerido
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-900 dark:text-white text-sm">
                      {employee.direccion}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Pestañas con el estilo de Configuración: subrayado #477FFA en la
            activa, no botones azules. */}
        <nav
          className="flex flex-wrap gap-1 border-b border-[#E0E0E0] my-8"
          aria-label="Secciones del expediente"
        >
          {([
            { id: "Emergencia", nombre: "Contacto de emergencia" },
            { id: "InfoLaboral", nombre: "Información laboral" },
            { id: "Expediente", nombre: "Expediente" },
            { id: "DocOpcionales", nombre: "Documentos opcionales" },
            { id: "AccionesAdmin", nombre: "Acciones administrativas" },
            { id: "Contratos", nombre: "Contratos" },
          ] as const).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setSelectedButtonMenu(tab.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm text-[#1E1E1F] transition-colors border-b-2 -mb-px ${
                selectedButtonMenu === tab.id
                  ? "border-[#477FFA] font-semibold"
                  : "border-transparent hover:border-gray-200"
              }`}
            >
              {tab.nombre}
            </button>
          ))}
        </nav>

        {/* Contacto de emergencia: ahora es la primera pestaña. */}
        <div
          className={`space-y-6 mb-8 ${
            selectedButtonMenu !== "Emergencia" && "hidden"
          }`}
        >
          {/* Card: Contacto de Emergencia */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-1 border-gray-200 mb-6">
            <div className="flex items-center justify-between mb-4 border-b border-gray-200 p-6">
              <h2 className="text-base font-semibold text-[#1E1E1F] flex items-center">
                <Phone className="w-5 h-5 mr-2" />
                Contacto de Emergencia
              </h2>
            </div>

            <div className="flex justify-between flex-wrap p-6 pt-0">
              {employee.emergenciaContactoNombre && !isEmergencyEditing ? (
                <div className="w-1/3 px-1 mt-4">
                  <p className="text-sm text-[#777777]">Nombre:</p>
                  <p className="text-gray-900 dark:text-white">
                    {employee.emergenciaContactoNombre}
                  </p>
                </div>
              ) : (
                <div className="w-1/3 px-1 mt-4">
                  <p className="text-sm text-[#777777]">Nombre:</p>
                  <div>
                    <input
                      {...register("emergenciaContactoNombre", {
                        required: false,
                      })}
                      id="emergenciaContactoNombre"
                      name="emergenciaContactoNombre"
                      className="w-full h-10 px-3 border-0 rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-1 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                      placeholder="-----"
                    />
                  </div>
                </div>
              )}

              {employee.emergenciaContactoRelacion && !isEmergencyEditing ? (
                <div className="w-1/3 px-1 mt-4">
                  <p className="text-sm text-[#777777]">Relación:</p>
                  <p className="text-gray-900 dark:text-white">
                    {employee.emergenciaContactoRelacion}
                  </p>
                </div>
              ) : (
                <div className="w-1/3 px-1 mt-4">
                  <p className="text-sm text-[#777777]">Relación:</p>
                  <div>
                    <input
                      {...register("emergenciaContactoRelacion", {
                        required: false,
                      })}
                      id="emergenciaContactoRelacion"
                      name="emergenciaContactoRelacion"
                      className="w-full h-10 px-3 border-0 rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-1 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                      placeholder="-----"
                    />
                  </div>
                </div>
              )}

              {employee.emergenciaContactoTelefono && !isEmergencyEditing ? (
                <div className="w-1/3 px-1 mt-4">
                  <p className="text-sm text-[#777777]">Teléfono:</p>
                  <p className="text-gray-900 dark:text-white">
                    {employee.emergenciaContactoTelefono}
                  </p>
                </div>
              ) : (
                <div className="w-1/3 px-1 mt-4">
                  <p className="text-sm text-[#777777]">Teléfono:</p>
                  <div>
                    <input
                      {...register("emergenciaContactoTelefono", {
                        required: false,
                      })}
                      id="emergenciaContactoTelefono"
                      name="emergenciaContactoTelefono"
                      className="w-full h-10 px-3 border-0 rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-1 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                      placeholder="-----"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Información Laboral */}
        <div
          className={`space-y-6 mb-8 ${
            selectedButtonMenu !== "InfoLaboral" && "hidden"
          }`}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-1 border-gray-200 pb-[200px]">
            <div className="flex items-center justify-between border-b border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                Información Laboral
              </h2>
              <div className="flex items-center gap-3">
                <Badge color="gris">Preregistro</Badge>
              </div>
            </div>

            <div className="space-y-4 flex items-start flex-wrap p-6">
              <div className="w-1/4 px-1 mt-4">
                <p className="text-sm text-[#777777]">Area:</p>
                {isLaboralEditing ? (
                  <div>
                    <Controller
                      name="areaId"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <SelectBuscable
                          variante="formulario"
                          id="areaId"
                          name="areaId"
                          opciones={areas.map((area) => ({ value: String(area.id), label: area.nombre }))}
                          value={String(field.value ?? "")}
                          onChange={field.onChange}
                          placeholder="Seleccione un Departamento/Área"
                          canDelete={false}
                          placeholderDeBusqueda="Buscar área..."
                        />
                      )}
                    />

                    {errors.areaId && (
                      <p className="text-red-500 text-xs mt-1">
                        Este campo es requerido
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-900 dark:text-white">
                    {employee.area?.nombre || "-"}
                  </p>
                )}
              </div>

              <div className="w-1/4 px-1 mt-4">
                <p className="text-sm text-[#777777]">Puesto:</p>
                {isLaboralEditing ? (
                  <div>
                    <Controller
                      name="puestoId"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <SelectBuscable
                          variante="formulario"
                          id="puestoId"
                          name="puestoId"
                          opciones={puestosFiltrados.map((puesto) => ({ value: String(puesto.id), label: puesto.nombre }))}
                          value={String(field.value ?? "")}
                          onChange={field.onChange}
                          placeholder={watch("areaId") ? "Primero seleccione un Departamento/Área" : "Seleccione un puesto"}
                          canDelete={false}
                          placeholderDeBusqueda="Buscar puesto..."
                        />
                      )}
                    />
                    {errors.puestoId && (
                      <p className="text-red-500 text-xs mt-1">
                        Este campo es requerido
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-900 dark:text-white">
                    {isNullishText(employee.puesto?.nombre)
                      ? "Sin asignar"
                      : `${employee.puesto?.nombre}${
                          !isNullishText(
                            employee.puesto?.nivel as string | null,
                          )
                            ? ` - Nivel ${employee.puesto?.nivel}`
                            : ""
                        }`}
                  </p>
                )}
              </div>

              <div className="w-1/4 px-1 mt-4">
                <p className="text-sm text-[#777777]">Fecha de Ingreso:</p>
                {isLaboralEditing ? (
                  <div>
                    <input
                      {...register("fechaIngreso", { required: true })}
                      id="fechaIngreso"
                      name="fechaIngreso"
                      type="date"
                      className="w-full h-10 px-3 border-0 rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-1 transition-all duration-200 text-sm text-gray-900"
                    />
                    {errors.fechaIngreso && (
                      <p className="text-red-500 text-xs mt-1">
                        Este campo es requerido
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-900 dark:text-white">
                    {formatDate(employee.fechaIngreso)}
                  </p>
                )}
              </div>

              <div className="w-1/4 px-1 mt-4">
                <p className="text-sm text-[#777777]">Tipo de Contrato:</p>
                {isLaboralEditing ? (
                  <div>
                    <Controller
                      name="tipoContrato"
                      control={control}
                      rules={{ required: false }}
                      render={({ field }) => (
                        <SelectBuscable
                          variante="formulario"
                          id="tipoContrato"
                          name="tipoContrato"
                          opciones={[
                            { value: "tiempo_completo", label: "Tiempo completo" },
                            { value: "medio_tiempo", label: "Medio tiempo" },
                            { value: "practicas", label: "Prácticas profesionales" },
                            { value: "por_proyecto", label: "Por proyecto" },
                          ]}
                          value={String(field.value ?? "")}
                          onChange={field.onChange}
                          canDelete={false}
                          placeholderDeBusqueda="Buscar tipo de contrato..."
                        />
                      )}
                    />
                    {errors.tipoContrato && (
                      <p className="text-red-500 text-xs mt-1">
                        Este campo es requerido
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-900 dark:text-white">
                    {formatDisplayText(employee.tipoContrato)}
                  </p>
                )}
              </div>

              <div className="w-1/4 px-1 mt-4">
                <p className="text-sm text-[#777777]">Jornada:</p>
                {isLaboralEditing ? (
                  <div>
                    <Controller
                      name="jornada"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <SelectBuscable
                          variante="formulario"
                          id="jornada"
                          name="jornada"
                          opciones={[
                            { value: "tiempo_completo", label: "Tiempo completo" },
                            { value: "medio_tiempo", label: "Medio tiempo" },
                            { value: "practicas", label: "Prácticas profesionales" },
                            { value: "por_proyecto", label: "Por proyecto" },
                          ]}
                          value={String(field.value ?? "")}
                          onChange={field.onChange}
                          canDelete={false}
                          placeholderDeBusqueda="Buscar jornada..."
                        />
                      )}
                    />

                    {errors.jornada && (
                      <p className="text-red-500 text-xs mt-1">
                        Este campo es requerido
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-900 dark:text-white">
                    {formatDisplayText(employee.jornada)}
                  </p>
                )}
              </div>

              <div className="w-1/4 px-1 mt-4">
                <p className="text-sm text-[#777777]"> Sueldo Bruto Mensual:</p>
                {isLaboralEditing ? (
                  <div>
                    <input
                      {...register("sueldoBrutoMensual", { required: true })}
                      id="sueldoBrutoMensual"
                      name="sueldoBrutoMensual"
                      className="w-full h-10 px-3 border-0 rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-1 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                      placeholder="-----"
                    />
                    {errors.sueldoBrutoMensual && (
                      <p className="text-red-500 text-xs mt-1">
                        Este campo es requerido
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-900 dark:text-white">
                    $
                    {employee.sueldoBrutoMensual.toLocaleString("es-MX", {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                )}
              </div>

              {isLaboralEditing && (
                <div className="w-1/4 px-1 mt-4">
                  <p className="text-sm text-[#777777]"> Jefe inmediato:</p>
                  <div>
                    <Controller
                      name="jefeInmediatoId"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <SelectBuscable
                          variante="formulario"
                          id="jefeInmediatoId"
                          name="jefeInmediatoId"
                          opciones={usuarios.map((usuario) => ({ value: String(usuario.id), label: `${usuario.firstName} ${usuario.lastName} (${usuario.email})` }))}
                          value={String(field.value ?? "")}
                          onChange={field.onChange}
                          placeholder="Seleccionar..."
                          canDelete={false}
                          placeholderDeBusqueda="Buscar usuario..."
                        />
                      )}
                    />

                    {errors.jefeInmediatoId && (
                      <p className="text-red-500 text-xs mt-1">
                        Este campo es requerido
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="w-1/4 px-1 mt-4">
                <p className="text-sm text-[#777777]"> Prestaciones:</p>
                {isLaboralEditing ? (
                  <div>
                    <input
                      {...register("prestaciones", { required: true })}
                      id="prestaciones"
                      name="prestaciones"
                      className="w-full h-10 px-3 border-0 rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-1 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                      placeholder="-----"
                    />
                    {errors.prestaciones && (
                      <p className="text-red-500 text-xs mt-1">
                        Este campo es requerido
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-900 dark:text-white">
                    {employee.prestaciones}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Expediente. Sin tarjeta ni título propios: el expediente ya trae sus
            propias cajas, y envolverlo metía un borde dentro de otro y repetía
            el nombre que ya dice la pestaña. */}
        <div
          className={`space-y-6 mb-8 ${
            selectedButtonMenu !== "Expediente" && "hidden"
          }`}
        >
          <EmployeeDigitalFile isEditable={isExpedienteEditing} />
        </div>

        {/* Documentos Opcionales */}
        <div
          className={`space-y-6 mb-8 ${
            selectedButtonMenu !== "DocOpcionales" && "hidden"
          }`}
        >
          {/* Sin onEditToggle: con un solo modo de edición ya no tiene sentido
              que esta sección prenda el suyo por su cuenta. */}
          <EmployeeDigitalFile
            showOptionalDocumentsOnly={true}
            isEditable={isOptionalDocsEditing}
          />
        </div>

        {/* Acciones Administrativas */}
        {selectedButtonMenu === "AccionesAdmin" && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-1 border-gray-200 mb-8">
              <div className="flex items-center justify-between mb-4 border-b border-gray-200 p-6">
                <h2 className="text-base font-semibold text-[#1E1E1F] flex items-center">
                  Acciones administrativas
                </h2>
              </div>
              <div className="space-y-4 p-6">
                {/* IMSS */}
                <div className="max-w-[412px] p-4 bg-[#ffffff] rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-[#414141] text-sm">IMSS</p>
                    {canEnableImss && isAdminActionsEditing && (
                      <>
                        {employee.habilitadoImss ? (
                          <button
                            onClick={() => setShowImssModal(true)}
                            type="button"
                            className={estiloDeBoton('peligro', 'mx-3')}
                          >
                            Deshabilitar
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => setShowImssModal(true)}
                              type="button"
                              disabled={!imssCanEnable.canEnable}
                              className={estiloDeBoton('primario', 'mx-3')}
                            >
                              Habilitar
                            </button>
                            {!imssCanEnable.canEnable &&
                              imssCanEnable.reasons.length > 0 && (
                                <div className="mt-2 text-xs text-orange-600 dark:text-orange-400">
                                  <p className="font-medium mb-1">
                                    Requisitos faltantes:
                                  </p>
                                  <ul className="list-disc ml-4 space-y-0.5">
                                    {imssCanEnable.reasons.map(
                                      (reason, idx) => (
                                        <li key={idx}>{reason}</li>
                                      ),
                                    )}
                                  </ul>
                                </div>
                              )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* ERP */}
                <div className="max-w-[412px] p-4 bg-[#ffffff] rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-[#414141] text-sm">ERP</p>
                    {canEnableErp && isAdminActionsEditing && (
                      <>
                        {employee.habilitadoErp ? (
                          <button
                            onClick={() => setShowErpModal(true)}
                            type="button"
                            className={estiloDeBoton('peligro', 'mx-3')}
                          >
                            Deshabilitar
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => setShowErpModal(true)}
                              type="button"
                              disabled={!erpCanEnable.canEnable}
                              className={estiloDeBoton('primario', 'mx-3')}
                            >
                              Habilitar
                            </button>
                            {!erpCanEnable.canEnable &&
                              erpCanEnable.reasons.length > 0 && (
                                <div className="mt-2 text-xs text-orange-600 dark:text-orange-400">
                                  <p className="font-medium mb-1">
                                    Requisitos faltantes:
                                  </p>
                                  <ul className="list-disc ml-4 space-y-0.5">
                                    {erpCanEnable.reasons.map((reason, idx) => (
                                      <li key={idx}>{reason}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Recalcular Completitud */}
                <button
                  type="button"
                  onClick={handleRecalculateCompleteness}
                  disabled={!isAdminActionsEditing}
                  className={estiloDeBoton('primario', 'min-w-[412px]')}
                >
                  Recalcular Progreso
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Contratos */}
        <div
          className={`space-y-6 mb-8 ${
            selectedButtonMenu !== "Contratos" && "hidden"
          }`}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-1 border-gray-200">
            <div className="flex items-center justify-between border-b border-gray-200 p-6">
              <h2 className="text-base font-semibold text-[#1E1E1F] flex items-center">
                Contratos
              </h2>
            </div>
            <div className="p-6">
              <EmployeeContractsDetail isEditable={isContractsEditing} />
            </div>
          </div>
        </div>

        {/* Un solo par de botones para toda la ficha: el modo de edición es
            global, así que guardar desde cualquier pestaña guarda todo. */}
        {editando && (
          <div className="flex items-center justify-end gap-3 mb-8">
            <button
              type="button"
              onClick={() => void cancelEditReloadData()}
              className={estiloDeBoton('secundario')}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => void handleSubmit(onSubmit)()}
              className={estiloDeBoton('primario')}
            >
              <Upload className="w-4 h-4" />
              Guardar
            </button>
          </div>
        )}
      </form>

      {/* Modals */}
      <ConfirmModal
        isOpen={showImssModal}
        onClose={() => setShowImssModal(false)}
        onConfirm={
          employee.habilitadoImss ? handleDisableImss : handleEnableImss
        }
        title={employee.habilitadoImss ? "Deshabilitar IMSS" : "Habilitar IMSS"}
        message={
          employee.habilitadoImss
            ? "¿Estás seguro de que deseas deshabilitar IMSS para este empleado?"
            : "¿Estás seguro de que deseas habilitar IMSS para este empleado? Asegúrate de que cumple con todos los requisitos."
        }
        confirmText={employee.habilitadoImss ? "Deshabilitar" : "Habilitar"}
        variant={employee.habilitadoImss ? "danger" : "info"}
        isLoading={isEnablingImss}
      />

      <ConfirmModal
        isOpen={showErpModal}
        onClose={() => setShowErpModal(false)}
        onConfirm={employee.habilitadoErp ? handleDisableErp : handleEnableErp}
        title={employee.habilitadoErp ? "Deshabilitar ERP" : "Habilitar ERP"}
        message={
          employee.habilitadoErp
            ? "¿Estás seguro de que deseas deshabilitar ERP para este empleado?"
            : "¿Estás seguro de que deseas habilitar ERP para este empleado? Asegúrate de que cumple con todos los requisitos."
        }
        confirmText={employee.habilitadoErp ? "Deshabilitar" : "Habilitar"}
        variant={employee.habilitadoErp ? "danger" : "info"}
        isLoading={isEnablingErp}
      />

      {/* Notification */}
      {notification?.isVisible && (
        <Notification
          type={notification.type}
          title={notification.title}
          message={notification.message}
          isVisible={notification.isVisible}
          onClose={hideNotification}
        />
      )}
    </div>
  );
};
